class NoshtLocalizationModal extends HTMLElement {
  constructor() {
    super();
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onCountrySearchInput = this.onCountrySearchInput.bind(this);
    this.onSearchResize = this.onSearchResize.bind(this);
    this.focusTimer = null;
  }

  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    document.addEventListener('click', this.onDocumentClick, {
      capture: true,
      signal: this.abortController.signal,
    });
    this.addEventListener('input', this.onCountrySearchInput, {
      signal: this.abortController.signal,
    });
    window.addEventListener('resize', this.onSearchResize, {
      signal: this.abortController.signal,
    });

    this.initializeCountrySearch();
    this.enhanceTargetFallback();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    window.clearTimeout(this.focusTimer);
  }

  onDocumentClick(event) {
    const trigger = event.target instanceof Element
      ? event.target.closest('[data-nosht-localization-trigger]')
      : null;
    if (!trigger) return;

    event.preventDefault();
    this.hydrateContent();
    this.resetCountrySearch();
    this.positionRequestedSection(trigger.dataset.localizationModalTarget);

    const modalApi = window.Woolman?.ModalsAndDrawers;
    if (!modalApi) {
      window.location.hash = this.id;
      return;
    }

    const parentDrawer = trigger.closest('.drawer.is-open');
    if (parentDrawer?.id) {
      modalApi.closeModalOrDrawer(parentDrawer.id);
    }

    modalApi.showModalOrDrawer(this.id);
    this.focusRequestedSection(trigger.dataset.localizationModalTarget);
  }

  enhanceTargetFallback() {
    if (window.location.hash !== `#${this.id}`) return;

    const modalApi = window.Woolman?.ModalsAndDrawers;
    if (!modalApi) return;

    this.hydrateContent();
    this.resetCountrySearch();
    this.positionRequestedSection('language');
    modalApi.showModalOrDrawer(this.id);
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}`
    );
  }

  hydrateContent() {
    if (this.dataset.localizationHydrated === 'true') return true;

    const modalBody = this.querySelector('[data-modal-body]');
    const fallback = this.querySelector('[data-localization-modal-fallback]');
    const source = fallback?.textContent?.trim();

    if (!modalBody || !fallback || !source) return false;

    const template = document.createElement('template');
    template.innerHTML = source;
    fallback.replaceWith(template.content);
    this.dataset.localizationHydrated = 'true';
    this.initializeCountrySearch();

    this.dispatchEvent(new CustomEvent('nosht:localization:hydrated', {
      bubbles: true,
    }));

    return true;
  }

  initializeCountrySearch() {
    this.countrySearch = this.querySelector('[data-country-search]');
    this.countrySection = this.querySelector('[data-country-selector]');
    this.countryEmpty = this.querySelector('[data-country-search-empty]');
    if (!this.countrySearch || !this.countrySection) return;

    const aliases = {
      FI: 'Finland Suomi',
      SE: 'Sweden Sverige Ruotsi',
      DE: 'Germany Deutschland Saksa Tyskland',
    };
    this.countryItems = Array.from(this.countrySection.querySelectorAll('[data-country-list] > li')).map((row) => {
      const button = row.querySelector('button[name="country_code"]');
      const countryName = row.querySelector('.country-name')?.textContent || '';
      const countryCode = button?.value || '';
      return {
        row,
        searchText: this.normalizeCountrySearch(`${countryName} ${countryCode} ${aliases[countryCode] || ''}`),
      };
    });
    this.querySelector('[data-country-search-control]').hidden = false;
    this.resetCountrySearch();
  }

  normalizeCountrySearch(value) {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  }

  onCountrySearchInput(event) {
    if (event.target !== this.countrySearch) return;
    this.filterCountries();
  }

  onSearchResize() {
    if (this.countrySearch?.value.trim()) this.filterCountries();
  }

  resetCountrySearch() {
    if (!this.countrySearch) return;
    this.countrySearch.value = '';
    this.filterCountries();
  }

  filterCountries() {
    if (!this.countrySearch || !this.countryItems) return;
    const query = this.normalizeCountrySearch(this.countrySearch.value);
    const scroller = this.querySelector('.localization-popover__scroll');
    const scrollTop = scroller?.scrollTop || 0;

    // Keep the search field still when a short result list would otherwise
    // shrink the centered dialog or clamp its country-shortcut scroll position.
    if (query && scroller) {
      const style = window.getComputedStyle(scroller);
      const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
      this.countrySection.style.minHeight = `${Math.max(0, scroller.clientHeight - padding)}px`;
    } else {
      this.countrySection.style.removeProperty('min-height');
    }

    let visibleCount = 0;
    this.countryItems.forEach(({ row, searchText }) => {
      const matches = !query || searchText.includes(query);
      row.hidden = !matches;
      if (matches) visibleCount += 1;
    });
    this.countryEmpty.hidden = !query || visibleCount !== 0;
    if (scroller) scroller.scrollTop = scrollTop;
  }

  positionRequestedSection(sectionName) {
    const scroller = this.querySelector('.localization-popover__scroll');
    if (!scroller) return;

    // Set the opening view while the dialog is still hidden. Never let a
    // section shortcut scroll the dialog/header or the underlying page.
    this.querySelector('.modal-dialog').scrollTop = 0;
    this.querySelector('[data-modal-body]').scrollTop = 0;
    scroller.scrollTop = 0;

    if (sectionName !== 'country') return;
    const heading = scroller.querySelector('[data-localization-section="country"]');
    if (!heading) return;

    const padding = parseFloat(window.getComputedStyle(scroller).paddingTop) || 0;
    scroller.scrollTop = Math.max(0,
      heading.getBoundingClientRect().top - scroller.getBoundingClientRect().top - padding
    );
  }

  focusRequestedSection(sectionName) {
    window.clearTimeout(this.focusTimer);
    if (!sectionName) return;

    this.focusTimer = window.setTimeout(() => {
      if (!this.classList.contains('is-open')) return;

      const heading = this.querySelector(
        `[data-localization-section="${sectionName}"]`
      );
      if (!heading) return;

      heading.focus({ preventScroll: true });
    }, 650);
  }
}

if (customElements.get('nosht-localization-modal') === undefined) {
  customElements.define('nosht-localization-modal', NoshtLocalizationModal);
}
